import { PrivacySection } from './types'

export const privacyData: PrivacySection[] = [
  {
    id: 1,
    title: 'Introduction',
    content: [
      'At PixieWorld, your trust means everything to us. We know that when you share personal details to create a storybook for your child, you are placing a lot of confidence in us, and we take that seriously.',
      'This Privacy Policy is here to explain, in plain language, what information we collect, how we use it, and the choices you have. We have tried to keep the legal jargon to a minimum so you can read this quickly and feel good about using our services.',
    ],
  },
  {
    id: 2,
    title: 'Information We Collect',
    content: [
      'We only collect information that helps us create magical, personalized storybooks for your family.',
    ],
    subsections: [
      {
        title: 'Information You Give Us',
        content: [
          'When you create an account, place an order, or get in touch, you may share things like:',
          '• Your name and email address, so we can keep you updated on your order',
          '• Payment details (credit card, billing address), so we can process your purchase securely',
          '• Your child\'s name, age, and appearance details, so we can personalize their story',
          '• A shipping address, so your book arrives at the right doorstep',
        ],
      },
      {
        title: 'Information Collected Automatically',
        content: [
          'When you browse our website, some technical information is collected automatically to help us keep things running smoothly:',
          '• Basic device info (browser type, operating system)',
          '• How you use our site (pages visited, features used)',
          '• Cookies, which we explain in more detail below',
        ],
      },
    ],
  },
  {
    id: 3,
    title: 'How We Use Your Information',
    content: [
      'Everything we collect is used to give you and your child the best possible experience:',
      '• Creating and delivering your personalized storybooks',
      '• Keeping you updated on your order status',
      '• Answering your questions and providing customer support',
      '• Improving our website, stories, and overall experience',
      '• Sending you occasional updates or offers (only if you opt in)',
      '• Keeping our platform safe and secure',
    ],
  },
  {
    id: 4,
    title: 'Cookies and Tracking',
    content: [
      'Cookies are small text files stored on your device that help our website work properly and give you a better experience.',
    ],
    subsections: [
      {
        title: 'What Cookies We Use',
        content: [
          '• Essential Cookies: These keep the site running (for example, remembering items in your cart)',
          '• Analytics Cookies: These help us understand which pages are popular so we can improve',
          '• Preference Cookies: These remember your settings so you do not have to set them every time',
          '• Marketing Cookies: These help us show you relevant ads, but only if you allow them',
        ],
      },
      {
        title: 'Your Cookie Choices',
        content: [
          'You are in control. You can adjust your cookie preferences through your browser settings or our cookie banner at any time.',
        ],
      },
    ],
  },
  {
    id: 5,
    title: 'Third-Party Services',
    content: [
      'We work with a few trusted partners to help run our business. Here is who they are and what they do:',
    ],
    subsections: [
      {
        title: 'Google Analytics',
        content: [
          'We use Google Analytics to understand how people use our website so we can make it better.',
          'This data is collected anonymously, meaning we see overall trends, not individual behavior.',
          'If you prefer not to be tracked, you can install the Google Analytics opt-out browser add-on.',
        ],
      },
      {
        title: 'Payment Processing',
        content: [
          'Your payment is handled by secure, industry-standard payment processors.',
          'We never store your full credit card number on our servers. Payment data is encrypted and processed in compliance with PCI-DSS standards.',
        ],
      },
      {
        title: 'Advertising Partners',
        content: [
          'We may work with advertising partners to show you relevant ads on other sites.',
          'You can manage your ad preferences through your browser settings or industry opt-out tools at any time.',
        ],
      },
    ],
  },
  {
    id: 6,
    title: 'How We Share Your Information',
    content: [
      'Let us be clear: we never sell or rent your personal information to anyone.',
      'We only share your information when it is necessary to deliver our services:',
      '• With printing and shipping partners, so your book gets made and delivered',
      '• With service providers who help us run our website and business',
      '• If required by law or to protect the safety of our users',
    ],
  },
  {
    id: 7,
    title: 'How We Protect Your Data',
    content: [
      'We take the security of your information seriously and use strong safeguards to keep it safe:',
      '• SSL encryption protects your data whenever it travels between your device and our servers',
      '• Our servers and databases are secured with industry-standard protections',
      '• We regularly review and update our security practices',
      '• Only authorized team members can access personal information, and only when needed',
      'No system is perfect, but we work hard to stay ahead of potential threats and keep your data secure.',
    ],
  },
  {
    id: 8,
    title: 'How Long We Keep Your Data',
    content: [
      'We keep your information only as long as we need it to provide our services and fulfill our obligations.',
      'When your data is no longer needed, we securely delete or anonymize it.',
      'You can request deletion of your personal information at any time by contacting us.',
    ],
  },
  {
    id: 9,
    title: 'Your Privacy Rights',
    content: [
      'You are in control of your data. Depending on where you live, you may have the right to:',
      '• Access: See what personal information we have about you',
      '• Correct: Fix any information that is inaccurate or incomplete',
      '• Delete: Ask us to remove your personal information',
      '• Restrict: Limit how we use your data',
      '• Portability: Get a copy of your data in a portable format',
      '• Opt-out: Unsubscribe from marketing emails at any time',
      'To exercise any of these rights, simply email us at support@pixie.education and we will be happy to help.',
    ],
  },
  {
    id: 10,
    title: 'Children\'s Privacy',
    content: [
      'Our storybooks are made for children, but our website is designed for parents and guardians to use.',
      'We do not knowingly collect personal information directly from children under 13.',
      'The personalization details you provide (like your child\'s name and appearance) are used solely to create their storybook.',
      'If you believe your child has provided us with personal information without your consent, please reach out to us and we will promptly remove it.',
    ],
  },
  {
    id: 11,
    title: 'International Data Transfers',
    content: [
      'If you are visiting us from outside our primary operating country, your information may be transferred and stored in a different location.',
      'Wherever your data is processed, we make sure it is protected with the same care described in this policy.',
    ],
  },
  {
    id: 12,
    title: 'Links to Other Websites',
    content: [
      'Our site may link to other websites that we do not operate.',
      'We are not responsible for the privacy practices of those sites, so we encourage you to check their privacy policies when you visit them.',
    ],
  },
  {
    id: 13,
    title: 'Artificial Intelligence',
    content: [
      'PixieWorld uses artificial intelligence (AI) to help create personalized stories and illustrations for your child. Here is what that means for your privacy:',
    ],
    subsections: [
      {
        title: 'How We Use AI',
        content: [
          'AI helps us generate unique story content and artwork tailored to the details you provide, such as your child\'s name, appearance, and story preferences.',
          'This is what makes each PixieWorld book truly one-of-a-kind.',
        ],
      },
      {
        title: 'Our AI Service Provider',
        content: [
          'We use Google as our third-party AI service provider. When you create a personalized storybook, relevant personalization details are shared with Google\'s AI services to generate your content.',
          'Google processes this data in accordance with their own privacy policies and data protection standards.',
          'We only share the information necessary to create your storybook, nothing more.',
        ],
      },
      {
        title: 'Quality and Safety',
        content: [
          'All AI-generated content is designed with children in mind. We have built our systems to produce age-appropriate, safe, and delightful stories and illustrations for your little ones.',
        ],
      },
    ],
  },
  {
    id: 14,
    title: 'Changes to This Privacy Policy',
    content: [
      'We may update this Privacy Policy from time to time as our services evolve or as regulations change.',
      'When we make meaningful changes, we will update the "Last Updated" date at the top of this page.',
      'We encourage you to check back occasionally, but we will always aim to keep our practices transparent and straightforward.',
    ],
  },
  {
    id: 15,
    title: 'Contact Us',
    content: [
      'Have a question about your privacy or this policy? We would love to hear from you.',
      'Email: support@pixie.education',
      'We aim to respond to all inquiries as quickly as possible.',
    ],
  },
]
